import { axiosClient } from '../../../library/Service/axiosClient';

const BASE = process.env.REACT_APP_INTERNAL_ADMIN_SERVER;

// Human-friendly relative phrasing for a "days pending" count (e.g. proposed today / yesterday / 3 days ago).
export const formatDaysAgo = (days: number | null | undefined): string => {
    if (days == null) return '';
    if (days <= 0) return 'today';
    if (days === 1) return 'yesterday';
    return `${days} days ago`;
};

// Plain-English reasoning for an activation stage (mirrors the backend STAGE_REASONING), used to
// explain WHY a stage transition was proposed instead of showing the raw rule expression.
export const STAGE_REASONING: Record<string, string> = {
    identified: 'In a strategic watchlist; contact not yet verified.',
    validated: 'Strategic watchlist member with verified contact.',
    warmed: 'Verified contact with a recent engagement signal.',
    engaged: 'Outcome-specific engagement recorded — actively responding.',
    qualified: 'Committed (said yes) but has not acted yet.',
    contribution: 'Committed and has contributed at least once.',
    dormant: 'In the reactivation quadrant — went quiet.',
    denied: 'Explicitly declined this commitment.',
};
export const stageReason = (stage?: string | null): string | null => (stage ? STAGE_REASONING[stage] || null : null);

export interface HitlQueueItem {
    queue_id: number;
    review_type: 'stage_transition' | 'pool_classification';
    institute_id: number;
    student_id: string;
    name: string | null;
    program_and_year: string;
    campus_name: string | null;
    layer: string;
    outcome: string | null;
    outcome_label: string | null;
    pool_code: string | null;
    pool_label: string | null;
    current_value: string | null;
    proposed_value: string | null;
    current_stage: string | null;
    current_stage_label: string | null;
    proposed_stage: string | null;
    proposed_stage_label: string | null;
    ambiguity_flag: string | null;
    ambiguity_reason: string | null;
    rule_trace: string | null;
    status: string;
    proposed_at: string | null;
    expires_at: string | null;
    days_pending: number | null;
    overall_ability: number | null;
    overall_willingness: number | null;
    overall_involvement: number | null;
    config?: { reason_required: boolean; notes_required: boolean };
}

export interface HitlQueueResponse {
    filters_applied: Record<string, any>;
    total_count: number;
    page: number;
    per_page: number;
    total_pages: number;
    items: HitlQueueItem[];
}

export interface HitlSummary {
    institute_id: number;
    pending_count: number;
    approved_count_last_30_days: number;
    rejected_count_last_30_days: number;
    expired_count_last_30_days: number;
    by_outcome: Record<string, Record<string, number>>;
    by_proposed_stage: Record<string, { pending: number }>;
}

export interface HitlConfig {
    id: number;
    hitl_enabled: boolean;
    hitl_required_transitions: Array<{ layer: string; outcome: string; target_stage: string; reason_required?: boolean; notes_required?: boolean }>;
    signal_pool_hitl_enabled: boolean;
    signal_pool_hitl_config: Record<string, { ambiguity_review_required?: boolean; removal_review_required?: boolean }>;
    review_expiry_days: number | null;
    auto_action_on_expiry: 'reject' | 'approve' | 'none';
    notes: string | null;
}

export interface QueueFilters {
    status?: string;
    review_type?: string;
    outcome?: string;
    proposed_stage?: string;
    pool_code?: string;
    ambiguity_flag?: string;
    page?: number;
    per_page?: number;
}

const clean = (o: Record<string, any>) => {
    const out: Record<string, any> = {};
    Object.entries(o).forEach(([k, v]) => { if (v != null && v !== '') out[k] = v; });
    return out;
};

class HitlService {
    static getQueue(instituteId: string | number, filters: QueueFilters = {}, filterInstituteId?: string | null): Promise<HitlQueueResponse> {
        const params = clean({ institute_id: instituteId, filter_institute_id: filterInstituteId, ...filters });
        return axiosClient().get(`${BASE}/watchlists/hitl/review-queue`, { params }).then((r) => r.data as HitlQueueResponse);
    }

    static getSummary(instituteId: string | number, filterInstituteId?: string | null): Promise<HitlSummary> {
        const params = clean({ institute_id: instituteId, filter_institute_id: filterInstituteId });
        return axiosClient().get(`${BASE}/watchlists/hitl/summary`, { params }).then((r) => r.data as HitlSummary);
    }

    static approve(queueId: number, body: { reason?: string; notes?: string }) {
        return axiosClient().post(`${BASE}/watchlists/hitl/review-queue/${queueId}/approve`, body).then((r) => r.data);
    }
    static reject(queueId: number, body: { reason?: string; notes?: string }) {
        return axiosClient().post(`${BASE}/watchlists/hitl/review-queue/${queueId}/reject`, body).then((r) => r.data);
    }
    static bulkApprove(queueIds: number[], body: { reason?: string; notes?: string }) {
        return axiosClient().post(`${BASE}/watchlists/hitl/review-queue/bulk-approve`, { queue_ids: queueIds, ...body }).then((r) => r.data);
    }
    static bulkReject(queueIds: number[], body: { reason?: string; notes?: string }) {
        return axiosClient().post(`${BASE}/watchlists/hitl/review-queue/bulk-reject`, { queue_ids: queueIds, ...body }).then((r) => r.data);
    }
    static getQueueHistory(queueId: number) {
        return axiosClient().get(`${BASE}/watchlists/hitl/review-queue/${queueId}/history`).then((r) => r.data);
    }

    static getConfig(): Promise<HitlConfig> {
        return axiosClient().get(`${BASE}/watchlists/hitl/config`).then((r) => r.data as HitlConfig);
    }
    static updateConfig(body: Partial<HitlConfig> & { change_reason?: string }) {
        return axiosClient().put(`${BASE}/watchlists/hitl/config`, body).then((r) => r.data);
    }
    static getConfigHistory() {
        return axiosClient().get(`${BASE}/watchlists/hitl/config/history`).then((r) => r.data);
    }
}

export const isSuperAdmin = () => sessionStorage.getItem('user-type-code') === 'SUPER_ADMIN';

export default HitlService;
